import { useEffect, useMemo, useState } from 'react'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { useParams } from 'react-router-dom'
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Eye,
  Info,
  Loader2,
  MessageCircle,
  Settings2,
  Trash2,
} from 'lucide-react'

import { AutomationTemplateConfigureBar } from '@/components/settings/AutomationTemplateConfigureBar'
import { AutomationTemplateMetaAlerts } from '@/components/settings/AutomationTemplateMetaAlerts'
import { AUTOMATION_TEMPLATE_UI } from '@/components/settings/automationTemplatesUi'
import {
  buildPortalVariableMapping,
  normalizePortalMappingForApi,
  normalizePortalMappingFromApi,
  previewPortalTemplateBody,
  type PortalLeadVariableMappingEntry,
} from '@/components/settings/portalLeadTemplateMapping'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { TemplateVariablesHelpDialog } from '@/components/actions/TemplateVariablesHelpDialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { META_INTEGRATION_ENABLED } from '@/config/env'
import {
  PORTAL_LEAD_WEBHOOK_FIELDS,
  portalLeadWebhookFieldExample,
} from '@/constants/portalLeadWebhookFields'
import { useAuth } from '@/hooks/use-auth'
import { log } from '@/lib/logger'
import { useWhatsAppStatusQuery } from '@/queries'
import { whatsappService, type WABA, type WhatsAppTemplate } from '@/services/whatsappService'

type PortalLeadLink = {
  id: number
  waba_id: string
  waba_name: string
  phone_number_id: string
  display_phone_number: string
  template_name: string
  template_language: string
  is_active: boolean
  variable_mapping: PortalLeadVariableMappingEntry[]
  configured_by_name: string
  updated_at: string
}

type DialogDraft = {
  waba_id: string
  template_key: string
  body_parameter_count: number
  variable_mapping: PortalLeadVariableMappingEntry[]
}

const EMPTY_DRAFT: DialogDraft = {
  waba_id: '',
  template_key: '',
  body_parameter_count: 0,
  variable_mapping: [],
}

function countTemplateBodyVariables(template: WhatsAppTemplate): number {
  const body = template.components.find((component) => component.type?.toUpperCase() === 'BODY')
  if (!body?.text) return 0
  const matches = body.text.match(/\{\{\d+\}\}/g)
  return matches ? matches.length : 0
}

function extractTemplateBodyText(template: WhatsAppTemplate | undefined): string {
  const body = template?.components.find((component) => component.type?.toUpperCase() === 'BODY')
  return body?.text || ''
}

function buildVariableMapping(
  count: number,
  previous: PortalLeadVariableMappingEntry[],
): PortalLeadVariableMappingEntry[] {
  return buildPortalVariableMapping(count, previous)
}

function describeWabaOption(waba: WABA): string {
  return [waba.name || waba.id, waba.display_phone_number]
    .filter(Boolean)
    .join(' · ')
}

function isSendableWaba(waba: WABA): boolean {
  return Boolean(waba.phone_number_id && String(waba.phone_number_id).trim())
}

export function PortalLeadSettingsCard() {
  const { lang = 'en' } = useParams()
  const { token } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const { data: whatsappStatus, isLoading: whatsappStatusLoading } = useWhatsAppStatusQuery({
    enabled: META_INTEGRATION_ENABLED && Boolean(token && token.trim()),
  })
  const whatsappConnected = Boolean(whatsappStatus?.connected)
  const canConfigureTemplates = META_INTEGRATION_ENABLED && whatsappConnected

  const [link, setLink] = useState<PortalLeadLink | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<DialogDraft>(EMPTY_DRAFT)
  const [wabas, setWabas] = useState<WABA[]>([])
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const hasAuthToken = Boolean(token && token.trim())
  const sendableWabas = useMemo(() => wabas.filter(isSendableWaba), [wabas])

  const helpVariables = useMemo(
    () =>
      PORTAL_LEAD_WEBHOOK_FIELDS.map((field) => ({
        slot: field.key,
        label: field.label,
        // La referencia no viaja en el webhook: se resuelve contra iagestión a
        // partir de IdInmueble, así que no debe anunciarse como "LeadsPortal →".
        source:
          field.key === 'property_ref' ? field.source : `LeadsPortal → ${field.source}`,
        example: portalLeadWebhookFieldExample(field.key) ?? '…',
      })),
    [],
  )

  const loadLink = async () => {
    if (!hasAuthToken) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/v1/leads/portal-lead/link/', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (!response.ok) {
        if (response.status === 404) {
          setLink(null)
          return
        }
        throw new Error(`Failed to load Portal Lead config: ${response.status}`)
      }
      const data = (await response.json()) as PortalLeadLink | null
      setLink(data && typeof data === 'object' && 'id' in data ? data : null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load Portal Lead config'
      log.error('Portal Lead config load failed', { message })
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadLink()
  }, [hasAuthToken, token])

  const loadTemplates = async (wabaId: string) => {
    setTemplates([])
    try {
      const data = await whatsappService.getTemplates(wabaId)
      setTemplates(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates'
      log.error('Portal Lead template load failed', { message, wabaId })
      setDialogError(message)
    }
  }

  const openDialog = async () => {
    setDialogError(null)
    setPreviewText(null)
    setDraft({
      waba_id: link?.waba_id ?? '',
      template_key: link ? `${link.template_name}:${link.template_language}` : '',
      body_parameter_count: 0,
      variable_mapping: normalizePortalMappingFromApi(link?.variable_mapping),
    })
    setDialogOpen(true)
    setDialogLoading(true)

    try {
      const availableWabas = await whatsappService.getWabas()
      setWabas(availableWabas)
      const eligibleWabas = availableWabas.filter(isSendableWaba)
      const preferredWabaId = (link && eligibleWabas.some((item) => item.id === link.waba_id)
        ? link.waba_id
        : eligibleWabas[0]?.id) || ''
      setDraft((prev) => ({ ...prev, waba_id: preferredWabaId }))
      if (!eligibleWabas.length) {
        setDialogError('No hay una cuenta de WhatsApp conectada con phone number ID disponible.')
      }
      if (preferredWabaId) {
        const loadedTemplates = await whatsappService.getTemplates(preferredWabaId)
        setTemplates(loadedTemplates)
        if (link) {
          const selected = loadedTemplates.find(
            (item) => `${item.name}:${item.language}` === `${link.template_name}:${link.template_language}`,
          )
          if (selected) {
            const count = countTemplateBodyVariables(selected)
            setDraft((prev) => ({
              ...prev,
              body_parameter_count: count,
              variable_mapping: buildVariableMapping(
                count,
                normalizePortalMappingFromApi(link.variable_mapping),
              ),
            }))
          }
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load WhatsApp accounts'
      log.error('Portal Lead WABA load failed', { message })
      setDialogError(message)
    } finally {
      setDialogLoading(false)
    }
  }

  const handleWabaChange = async (value: string) => {
    setDraft((prev) => ({
      ...prev,
      waba_id: value,
      template_key: '',
      body_parameter_count: 0,
      variable_mapping: [],
    }))
    setDialogError(null)
    await loadTemplates(value)
  }

  const handleTemplateChange = (templateKey: string) => {
    const selectedTemplate = templates.find(
      (item) => `${item.name}:${item.language}` === templateKey,
    )
    const count = selectedTemplate ? countTemplateBodyVariables(selectedTemplate) : 0
    setPreviewText(null)
    setDraft((prev) => ({
      ...prev,
      template_key: templateKey,
      body_parameter_count: count,
      variable_mapping: buildVariableMapping(count, prev.variable_mapping),
    }))
  }

  const handlePreview = () => {
    setPreviewLoading(true)
    setDialogError(null)
    try {
      const bodyText = extractTemplateBodyText(selectedTemplate)
      if (!bodyText) {
        setPreviewText('La plantilla seleccionada no tiene cuerpo de texto.')
        return
      }
      const { preview, missingPositions } = previewPortalTemplateBody(bodyText, draft.variable_mapping)
      setPreviewText(
        missingPositions.length
          ? `Faltan: ${missingPositions.map((position) => `{{${position}}}`).join(', ')}`
          : `Vista previa: ${preview}`,
      )
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleMappingChange = (position: number, sourceKey: string) => {
    setDraft((prev) => ({
      ...prev,
      variable_mapping: prev.variable_mapping.map((item) =>
        item.position === position
          ? { ...item, source_type: 'portal_field', source_key: sourceKey }
          : item,
      ),
    }))
  }

  const handleSave = async () => {
    const selectedWaba = sendableWabas.find((item) => item.id === draft.waba_id)
    const selectedTemplate = templates.find(
      (item) => `${item.name}:${item.language}` === draft.template_key,
    )

    if (!selectedWaba || !selectedTemplate) {
      setDialogError('Selecciona una cuenta de WhatsApp y una plantilla.')
      return
    }
    if (!selectedWaba.phone_number_id) {
      setDialogError('La cuenta de WhatsApp seleccionada no tiene phone number ID.')
      return
    }
    if (
      draft.body_parameter_count > 0 &&
      draft.variable_mapping.some((item) => !item.source_key)
    ) {
      setDialogError('Mapea cada variable de la plantilla a un campo del webhook.')
      return
    }

    setSaving(true)
    setDialogError(null)
    try {
      const response = await fetch('/api/v1/leads/portal-lead/link/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          waba_id: selectedWaba.id,
          waba_name: selectedWaba.name || '',
          phone_number_id: selectedWaba.phone_number_id,
          display_phone_number: selectedWaba.display_phone_number || '',
          template_name: selectedTemplate.name,
          template_language: selectedTemplate.language,
          variable_mapping: normalizePortalMappingForApi(draft.variable_mapping),
        }),
      })
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload.error || payload.variable_mapping?.[0] || 'No se pudo guardar Portal Lead')
      }

      const saved = (await response.json()) as PortalLeadLink
      setLink(saved)
      setDialogOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo guardar Portal Lead'
      log.error('Portal Lead config save failed', { message })
      setDialogError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar la plantilla configurada para Portal Lead?')) return
    setRemoving(true)
    setError(null)
    try {
      const response = await fetch('/api/v1/leads/portal-lead/link/', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      if (!response.ok && response.status !== 404) {
        throw new Error('No se pudo eliminar la configuración')
      }
      setLink(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo eliminar la configuración'
      log.error('Portal Lead config delete failed', { message })
      setError(message)
    } finally {
      setRemoving(false)
    }
  }

  const handleToggleActive = async (isActive: boolean) => {
    if (!link) return
    setToggling(true)
    setError(null)
    try {
      const response = await fetch('/api/v1/leads/portal-lead/link/', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: isActive }),
      })
      if (!response.ok) {
        throw new Error('No se pudo actualizar la automatización')
      }
      const next = (await response.json()) as PortalLeadLink
      setLink(next)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo actualizar la automatización'
      log.error('Portal Lead config activation toggle failed', { message })
      setError(message)
    } finally {
      setToggling(false)
    }
  }

  const selectedTemplate = useMemo(
    () => templates.find((item) => `${item.name}:${item.language}` === draft.template_key),
    [templates, draft.template_key],
  )
  const selectedTemplateBody = extractTemplateBodyText(selectedTemplate)

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando configuración Portal Lead...
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <SeccionPlegable
        numero="02"
        etiqueta="Portal Lead"
        titulo="WhatsApp al lead del portal"
        descripcion="Cuando IA Gestión envía un lead por webhook, se dispara la plantilla de primer contacto."
        estado={
          <>
{link?.is_active ? (
                    <Badge variant="outline" className="ml-1 border-success/40 text-success">
                      Activa
                    </Badge>
                  ) : link ? (
                    <Badge variant="outline" className="ml-1">
                      Pausada
                    </Badge>
                  ) : null}
          </>
        }
        abierta={isOpen}
        onAlternar={() => setIsOpen((prev) => !prev)}
        acciones={
          <>
<Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className="shrink-0 rounded-full text-muted-foreground"
              aria-label="Ayuda variables Portal Lead"
              onClick={() => setHelpOpen(true)}
            >
              <Info className="h-4 w-4" />
            </Button>
          </>
        }
      >
              <div className="grid gap-2 md:grid-cols-3">
                <div className="space-y-1">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
                    <MessageCircle className="h-4 w-4 text-primary" />
                    1. Llega el webhook
                  </div>
                  <p className="text-xs text-muted-foreground">
                    IA Gestión envía <code>LeadsPortal</code> con nombre, móvil, portal e inmueble.
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    2. Se mapean variables
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Cada <code>{'{{n}}'}</code> de la plantilla se rellena con un campo del webhook configurado aquí.
                  </p>
                </div>
                <div className="space-y-1">
                  <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
                    <Building2 className="h-4 w-4 text-primary" />
                    3. Demanda RK
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tras el WhatsApp, el backend crea o edita la demanda en RK si la integración está configurada.
                  </p>
                </div>
              </div>

              <AutomationTemplateConfigureBar
                onAdd={() => void openDialog()}
                disabled={!hasAuthToken || !canConfigureTemplates}
              />

              <AutomationTemplateMetaAlerts
                lang={lang}
                metaIntegrationEnabled={META_INTEGRATION_ENABLED}
                whatsappConnected={whatsappConnected}
                whatsappStatusLoading={whatsappStatusLoading}
              />

              {META_INTEGRATION_ENABLED && whatsappConnected ? (
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="outline" className="border-success/40 text-success">
                    WhatsApp conectado
                  </Badge>
                  {whatsappStatus?.metadata?.display_phone_number ? (
                    <span>{whatsappStatus.metadata.display_phone_number}</span>
                  ) : null}
                  {whatsappStatus?.account_id ? (
                    <span className="text-xs">WABA {whatsappStatus.account_id}</span>
                  ) : null}
                </div>
              ) : null}

              {error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <div className="grid gap-4 space-y-2 p-4 md:grid-cols-1">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <Label htmlFor="portal-lead-automation">Automatización activa</Label>
                    <p className="text-xs text-muted-foreground">
                      Controla todo el flujo: procesamiento del lead, WhatsApp y creación/edición de demanda RK.
                      {!link ? ' Primero agrega una plantilla.' : ''}
                    </p>
                  </div>
                  <Switch
                    id="portal-lead-automation"
                    checked={link?.is_active ?? false}
                    disabled={!link || toggling}
                    onCheckedChange={(checked) => void handleToggleActive(checked)}
                  />
                </div>
              </div>

              {link ? (
                <div className="space-y-2">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plantilla</TableHead>
                        <TableHead>Idioma</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <div className="font-medium">{link.template_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {[link.waba_name || link.waba_id, link.display_phone_number]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </TableCell>
                        <TableCell>{link.template_language}</TableCell>
                        <TableCell>
                          <Badge variant={link.is_active ? 'default' : 'secondary'}>
                            {link.is_active ? 'Activa' : 'Pausada'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!canConfigureTemplates}
                              onClick={() => void openDialog()}
                            >
                              <Settings2 className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => void handleDelete()} disabled={removing}>
                              {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-background/70 px-4 py-8 text-center">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div className="text-sm font-medium text-foreground">Todavía no hay plantilla configurada</div>
                  <p className="mx-auto mt-1 max-w-xl text-sm text-muted-foreground">
                    {canConfigureTemplates
                      ? 'Agrega una plantilla aprobada y mapea sus variables al webhook LeadsPortal.'
                      : 'Cuando WhatsApp esté conectado, elige una plantilla aprobada y mapea sus variables.'}
                  </p>
                </div>
              )}
      </SeccionPlegable>

      <TemplateVariablesHelpDialog
        open={helpOpen}
        onOpenChange={setHelpOpen}
        title="Variables Portal Lead"
        description="Cada {{n}} de la plantilla Meta se asigna a un campo del webhook LeadsPortal al vincular la plantilla."
        orderNote="El teléfono del lead (Movil) no es una variable de plantilla: se usa como destinatario del WhatsApp."
        variablesTitle="Campos del webhook"
        sourceLabel="Origen:"
        variables={helpVariables}
        examples={[
          {
            title: 'Primer contacto portal',
            text: 'Hola {{1}}, recibimos tu solicitud desde {{2}} sobre la vivienda {{3}}.',
          },
        ]}
        note="Backend: IaGestionPortalFlow aplica el mapeo guardado en cada fila LeadsPortal entrante."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {link
                ? AUTOMATION_TEMPLATE_UI.editTemplateButton
                : AUTOMATION_TEMPLATE_UI.addTemplateButton}
            </DialogTitle>
          </DialogHeader>

          {dialogLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Cargando plantillas de Meta...
            </div>
          ) : (
            <div className="space-y-4">
              {dialogError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{dialogError}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-2">
                <Label>Cuenta de WhatsApp</Label>
                <Select value={draft.waba_id || undefined} onValueChange={(value) => void handleWabaChange(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar WABA" />
                  </SelectTrigger>
                  <SelectContent>
                    {sendableWabas.map((waba) => (
                      <SelectItem key={waba.id} value={waba.id}>
                        {describeWabaOption(waba)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Plantilla aprobada de Meta</Label>
                <Select
                  value={draft.template_key || undefined}
                  onValueChange={handleTemplateChange}
                  disabled={!draft.waba_id || sendableWabas.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={`${template.name}:${template.language}`} value={`${template.name}:${template.language}`}>
                        {template.name} ({template.language}) — {countTemplateBodyVariables(template)} vars
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTemplateBody ? (
                  <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                    {selectedTemplateBody}
                  </p>
                ) : null}
              </div>

              {draft.body_parameter_count > 0 ? (
                <div className="space-y-2">
                  <Label>Mapa de variables</Label>
                  <p className="text-xs text-muted-foreground">
                    Asigna cada hueco de la plantilla a un campo del webhook LeadsPortal.
                  </p>
                  {draft.variable_mapping.map((mapping) => (
                    <div key={mapping.position} className="grid grid-cols-[80px_1fr] items-center gap-2">
                      <span className="text-sm font-medium">{`{{${mapping.position}}}`}</span>
                      <Select
                        value={mapping.source_key || undefined}
                        onValueChange={(value) => handleMappingChange(mapping.position, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar campo del webhook" />
                        </SelectTrigger>
                        <SelectContent>
                          {PORTAL_LEAD_WEBHOOK_FIELDS.map((field) => (
                            <SelectItem key={field.key} value={field.key}>
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              ) : draft.template_key ? (
                <p className="text-xs text-muted-foreground">
                  Esta plantilla no tiene variables en el cuerpo. Se enviará tal cual cuando llegue un lead.
                </p>
              ) : null}

              {previewText ? (
                <Alert>
                  <AlertDescription>{previewText}</AlertDescription>
                </Alert>
              ) : null}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={previewLoading || dialogLoading || !draft.template_key}
              onClick={handlePreview}
            >
              {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
              Vista previa
            </Button>
            <Button type="button" disabled={saving || dialogLoading || !draft.template_key} onClick={() => void handleSave()}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
